/**
 * The Shopify session-token handshake used by the connect popup, and how its failures are worded for
 * a merchant.
 *
 * Extracted from ConnectShopifyScreen so the timeout and the retry classification can be tested
 * without driving the whole screen (ENG-6681).
 */
/** How long to wait for the iframe to answer a token request before giving up. */
const TOKEN_REQUEST_TIMEOUT_MS = 10_000;

/**
 * Asks the iframe to mint a Shopify session token, resolving with it and the shop it belongs to.
 *
 * A round trip per call, deliberately: Shopify ID tokens "expire one minute after they're issued",
 * and their docs say to fetch "a fresh one for each request rather than caching it" — App Bridge may
 * even hand back a cached token with less than a minute left. Anything that holds one across a
 * human pause is racing that clock (ENG-6681).
 */
export function requestShopifyToken(
  iframeOrigin: string,
): Promise<{ token: string; shopDomain: string | undefined }> {
  return new Promise((resolve, reject) => {
    const cleanup = () => {
      window.clearTimeout(timer);
      window.removeEventListener("message", listener);
    };
    const listener = (event: MessageEvent) => {
      if (event.origin !== iframeOrigin) return;
      const data = event.data as
        | {
            type?: string;
            token?: string;
            shopDomain?: string | null;
            message?: string;
          }
        | undefined;
      if (!data) return;
      if (data.type === "fs.connect.shopifyToken" && data.token) {
        cleanup();
        resolve({
          token: data.token,
          shopDomain: data.shopDomain ?? undefined,
        });
      } else if (data.type === "fs.connect.shopifyTokenError") {
        cleanup();
        reject(
          new Error(data.message ?? "Failed to mint a Shopify session token."),
        );
      }
    };
    const timer = window.setTimeout(() => {
      cleanup();
      reject(new Error("The Shopify app didn't respond."));
    }, TOKEN_REQUEST_TIMEOUT_MS);
    window.addEventListener("message", listener);
    window.opener?.postMessage(
      { type: "fs.connect.requestShopifyToken" },
      iframeOrigin,
    );
  });
}

/**
 * What the merchant is told, keyed on the backend's error code.
 *
 * The backend's own wording stays accurate for logs and support; a store owner reading "session
 * token is invalid or expired" learns nothing they can act on, and on the first live install it was
 * misread on the call and worked around by uninstalling the app (ENG-6681).
 */
export function merchantMessage(code: string, backendMessage: string): string {
  switch (code) {
    case "invalid_shopify_session_token":
      return "This window's connection to Shopify timed out. Try again — it only takes a moment.";
    case "link_request_failed":
      return "Couldn't reach FreightSimple. Check your connection and try again.";
    default:
      return backendMessage;
  }
}

/** Whether the merchant can clear this failure by pressing Try again, rather than getting help. */
export function isRetryable(code: string): boolean {
  return (
    code === "invalid_shopify_session_token" ||
    code === "shopify_token_unavailable" ||
    code === "link_request_failed"
  );
}
